import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { fetchMe, reportBootError } from './lib/api'
import { getStartParam } from './lib/telegram'
import { setLanguage } from './i18n'
import { AppShellSkeleton } from './components/Skeleton'
import BuyCrushPage from './pages/BuyCrushPage'
import BuyVIPPage from './pages/BuyVIPPage'
import LikesPage from './pages/LikesPage'
import MatchesPage from './pages/MatchesPage'
import OnboardingPage from './pages/OnboardingPage'
import PhotoUploadPage from './pages/PhotoUploadPage'
import ProfilePage from './pages/ProfilePage'
import SwipePage from './pages/SwipePage'
import WelcomePage from './pages/WelcomePage'
import { useUserStore } from './store/userStore'

function AppRoutes() {
  const { t } = useTranslation()
  const { me, loading, error, setMe, setError } = useUserStore()
  const navigate = useNavigate()

  useEffect(() => {
    fetchMe()
      .then((data) => {
        setMe(data)
        // The backend's persisted locale already honors a manual
        // SettingsSheet pick — let it win over whatever we guessed at boot.
        setLanguage(data.locale)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e))
        reportBootError(e)
      })
  }, [setMe, setError])

  // Consume the deep-link start_param whenever it actually changes — NOT a
  // one-shot-per-mount guard (that was the previous design). Two things
  // this has to hold at once:
  //
  // 1. react-router's useNavigate() identity changes on every location
  //    change (v6.28), so the effect below re-runs on every navigation
  //    regardless. Since start_param stays constant for the whole Mini App
  //    session, comparing against lastStartParam (not a boolean) makes a
  //    same-value re-run a no-op — otherwise it would re-navigate back into
  //    the target screen every time the user backs out of it, making Back
  //    appear to loop instead of returning where it should (this is the
  //    original bug the one-shot guard was written to fix — still fixed
  //    here, just via value comparison instead of "ran once ever").
  // 2. Telegram can bring an already-open Mini App WebView back to the
  //    foreground (re-opened via a NEW ?startapp=... Direct Link) WITHOUT a
  //    full page reload — a real-world gap found via production testing:
  //    the bot's deep-link buttons (pay_crush/pay_vip etc.) opened the
  //    Mini App to whatever page the user had last left it on instead of
  //    the intended page, because a one-shot-per-mount guard never got a
  //    second chance to read the new start_param. The visibilitychange
  //    listener below re-checks whenever the WebView regains focus, so if
  //    Telegram's bridge did update initDataUnsafe.start_param in place (it
  //    varies by client/version — this can't be fully verified without a
  //    live device, but it's the standard, low-risk mitigation for exactly
  //    this class of bug), the app catches it instead of staying stuck.
  const lastStartParam = useRef<string | undefined>(undefined)
  const checkDeepLink = useCallback(() => {
    const param = getStartParam()
    if (param === lastStartParam.current) return
    lastStartParam.current = param
    if (param?.startsWith('match_')) {
      // In-app chat is retired (docs/bot-migration-decision.md's "Phase
      // 1.5 — Chat retirement") — matched users message each other over a
      // direct Telegram DM instead, handed off by the bot's own match
      // notification. This link still exists in every match notification
      // ever sent, so it must keep resolving to *something* useful: the
      // matches list, opened straight to that match's profile sheet,
      // rather than a dead /chat/:id route.
      const matchId = param.slice('match_'.length)
      if (/^\d+$/.test(matchId)) navigate(`/matches?open=${matchId}`, { replace: true })
    } else if (param === 'vip_renew') {
      // Sent by the VIP lifecycle bot notifier's Renew button (see
      // internal/bot/handler/deeplink.go VIPRenewDeepLink) — lands on the
      // paywall with the renewal-specific headline instead of the default one.
      navigate('/buy-vip?reason=renew', { replace: true })
    } else if (param === 'pay_vip' || param === 'pay_crush') {
      // Sent by the payment-pending/payment-nudge bot notifiers' "Buka
      // Swipify" button (see internal/bot/handler/payment_pending_notifier.go
      // and payment_nudge_notifier.go) — lands back on the order's
      // QR/countdown screen, whose mount-time resume logic
      // (lib/pendingOrder.ts) picks the in-flight order back up from
      // localStorage.
      navigate(param === 'pay_vip' ? '/buy-vip' : '/buy-crush', { replace: true })
    } else if (param === 'likes') {
      // Sent by the "someone liked you" bot notifier's deep-link button
      // (see docs/monetization-roadmap.md "Kejar Premium Feature" #4) —
      // this is the main way a user discovers the feature exists at all.
      navigate('/likes', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    checkDeepLink()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkDeepLink()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [checkDeepLink])

  if (loading) {
    return <AppShellSkeleton />
  }

  if (error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-2 p-6 bg-tg-bg">
        <p className="text-tg-hint">{t('app.authError')}</p>
        <p className="font-mono text-sm text-tg-text">{error}</p>
        <p className="mt-4 text-xs text-tg-hint">{t('app.openInTelegram')}</p>
      </div>
    )
  }

  return (
    <>
      {me?.acting_as && (
        <div className="sticky top-0 z-50 bg-amber-500 px-3 py-1.5 text-center text-xs font-medium text-white">
          {t('app.actingAsBanner', { tier: t(`app.actingAs.${me.acting_as}`) })}
        </div>
      )}
      <Routes>
        {me?.profile_complete && me.has_photo ? (
          // Full app — profile complete and photo uploaded.
          <>
            <Route path="/swipe" element={<SwipePage />} />
            <Route path="/matches" element={<MatchesPage />} />
            <Route path="/likes" element={<LikesPage />} />
            <Route path="/photos" element={<PhotoUploadPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/buy-crush" element={<BuyCrushPage />} />
            <Route path="/buy-vip" element={<BuyVIPPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="*" element={<Navigate to="/swipe" replace />} />
          </>
        ) : me?.profile_complete ? (
          // Profile done but no photo yet — hold at /photos until one is uploaded.
          <>
            <Route path="/photos" element={<PhotoUploadPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="*" element={<Navigate to="/photos" replace />} />
          </>
        ) : (
          // No profile yet — send to the welcome/consent screen first; its
          // CTA is what takes the user into /onboarding.
          <>
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/photos" element={<PhotoUploadPage />} />
            <Route path="*" element={<Navigate to="/welcome" replace />} />
          </>
        )}
      </Routes>
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  )
}
